import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Chip,
    CircularProgress,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Snackbar
} from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Add, Edit, Delete, PhotoCamera } from '@mui/icons-material';
import { useVehicleTypes, useCreateVehicleType, useUpdateVehicleType, useDeleteVehicleType } from 'hooks/useVehicleTypes';
import fileService from 'api/fileService';
import { Avatar } from '@mui/material';

// Initial state for form
const initialState = {
    name: '',
    slug: '',
    feePerKm: '',
    icon: '',
    active: true
};

const VehicleTypeList = () => {
    const { data: vehicleTypes, isLoading } = useVehicleTypes();
    const createMutation = useCreateVehicleType();
    const updateMutation = useUpdateVehicleType();
    const deleteMutation = useDeleteVehicleType();

    const [openModal, setOpenModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(initialState);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    // Filtered internal list if needed, or just default response
    const rows = vehicleTypes?.data || [];

    // Handlers
    const handleOpenModal = (vehicleType = null) => {
        if (vehicleType) {
            setEditingId(vehicleType._id);
            setFormData({
                name: vehicleType.name,
                slug: vehicleType.slug,
                feePerKm: vehicleType.feePerKm,
                icon: vehicleType.icon || '',
                active: vehicleType.active
            });
            setPreviewUrl(vehicleType.icon || '');
        } else {
            setEditingId(null);
            setFormData(initialState);
            setPreviewUrl('');
        }
        setSelectedFile(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingId(null);
        setFormData(initialState);
        setSelectedFile(null);
        setPreviewUrl('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'feePerKm' ? parseFloat(value) || '' : value
        }));
    };

    // Auto-generate slug from name
    const handleNameChange = (e) => {
        const value = e.target.value;
        setFormData((prev) => ({
            ...prev,
            name: value,
            slug: value
                .toLowerCase()
                .trim()
                .replace(/[^\w\s-]/g, '') // remove non-word chars
                .replace(/[\s_-]+/g, '-') // replace spaces and underscores with -
                .replace(/^-+|-+$/g, '') // remove leading/trailing -
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsUploading(true);
        try {
            let iconUrl = formData.icon;

            if (selectedFile) {
                const uploadedUrl = await fileService.uploadFile(selectedFile);
                iconUrl = uploadedUrl.url;
            }

            const payload = { ...formData, icon: iconUrl };

            if (editingId) {
                await updateMutation.mutateAsync({ id: editingId, payload });
            } else {
                await createMutation.mutateAsync(payload);
            }
            handleCloseModal();
            toast.success(`Vehicle type ${editingId ? 'updated' : 'created'} successfully`);
        } catch (error) {
            console.error('Error saving vehicle type:', error);
            const message = error.response?.data?.message || error.message || 'Failed to save vehicle type';
            toast.error(message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this vehicle type?')) {
            try {
                await deleteMutation.mutateAsync(id);
            } catch (error) {
                console.error('Error deleting vehicle type:', error);
            }
        }
    };

    // (removed columns definition, standardizing via Table below)

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={5}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={800} color="primary.main">
                    Vehicle Types
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
                    Add Vehicle Type
                </Button>
            </Stack>

            <Card sx={{ p: 2, borderRadius: 3 }}>
                <TableContainer component={Paper} elevation={0}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <TableCell>
                                    <strong>Icon</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Vehicle Type</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Slug</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Fee / KM</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Status</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No vehicle types registered
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row._id} hover>
                                        <TableCell>
                                            <Avatar src={row.icon} variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}>
                                                <PhotoCamera fontSize="small" />
                                            </Avatar>
                                        </TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.slug}</TableCell>
                                        <TableCell>₦{row.feePerKm}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.active ? 'Active' : 'Inactive'}
                                                color={row.active ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <IconButton size="small" color="primary" onClick={() => handleOpenModal(row)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDelete(row._id)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Edit Vehicle Type' : 'Add New Vehicle Type'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Stack spacing={3}>
                            <TextField
                                label="Vehicle Name"
                                name="name"
                                value={formData.name}
                                onChange={handleNameChange}
                                placeholder="e.g. Minivan"
                                required
                                fullWidth
                            />

                            <TextField
                                label="Slug Identifier"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                placeholder="minivan"
                                required
                                fullWidth
                                disabled
                                helperText="Unique identifier (auto-generated from name)"
                            />

                            <TextField
                                label="Fee per Kilometer"
                                name="feePerKm"
                                type="number"
                                value={formData.feePerKm}
                                onChange={handleChange}
                                required
                                fullWidth
                                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                            />

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Vehicle Icon
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar
                                        src={previewUrl}
                                        variant="rounded"
                                        sx={{ width: 64, height: 64, border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <PhotoCamera />
                                    </Avatar>
                                    <Button variant="outlined" component="label" startIcon={<PhotoCamera />} disabled={isUploading}>
                                        {formData.icon || selectedFile ? 'Change Icon' : 'Upload Icon'}
                                        <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                                    </Button>
                                    {selectedFile && (
                                        <Typography variant="caption" color="text.secondary">
                                            {selectedFile.name}
                                        </Typography>
                                    )}
                                </Stack>
                                {isUploading && <CircularProgress size={24} sx={{ mt: 1 }} />}
                            </Box>

                            <TextField
                                select
                                label="Status"
                                name="active"
                                value={formData.active}
                                onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                                SelectProps={{ native: true }}
                                fullWidth
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </TextField>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleCloseModal} disabled={createMutation.isPending || updateMutation.isPending || isUploading}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={createMutation.isPending || updateMutation.isPending || isUploading}
                        >
                            {createMutation.isPending || updateMutation.isPending || isUploading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
            <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </Box>
    );
};

export default VehicleTypeList;
